import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Dropdown, Icon } from '~/components';
import { colors, weights } from '~/styles';
import { TRANSITION } from './AssistantContext';

const slideIn = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
`;

const slideOut = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-100%); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const EXPANDED_WIDTH = 257;
const COLLAPSED_WIDTH = 48;

const S = {
  Wrapper: styled.div`
    ${({ $overlay, $expanded, $slideOut }) =>
      $overlay
        ? `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 10;
      pointer-events: none;
    `
        : `
      flex-shrink: 0;
      width: ${$expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH}px;
      background: ${colors.grey5};
      display: flex;
      flex-direction: column;
      transition: width 200ms ease, margin-left ${TRANSITION};
      border-right: 1px solid ${colors.grey10};
      height: 100%;
      overflow: hidden;
      position: relative;
      margin-left: ${$slideOut ? `-${$expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH}px` : '0'};
    `}
  `,

  OverlayScrim: styled.div`
    position: absolute;
    top: 0;
    left: 0.75rem;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.2);
    z-index: 1;
    pointer-events: auto;
    animation: ${({ $closing }) => ($closing ? fadeOut : fadeIn)} 200ms ${({ $closing }) => ($closing ? 'ease-in' : 'ease-out')} forwards;
  `,

  OverlayContent: styled.div`
    position: absolute;
    top: 0;
    left: 0.75rem;
    bottom: 0;
    width: ${EXPANDED_WIDTH}px;
    background: ${colors.grey5};
    display: flex;
    flex-direction: column;
    overflow: visible;
    z-index: 2;
    border-right: 1px solid ${colors.grey10};
    box-shadow: 0.3125rem 0 1.5rem rgba(0, 0, 0, 0.2);
    pointer-events: auto;
    animation: ${({ $closing }) => ($closing ? slideOut : slideIn)} 200ms ${({ $closing }) => ($closing ? 'ease-in' : 'ease-out')} forwards;
  `,

  ExpandedHeader: styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    height: 2.625rem;
    padding: 0 0.75rem 0 1.125rem;
    flex-shrink: 0;
    font-weight: ${weights.normal};
    font-size: 1rem;
    color: ${colors.black};
    white-space: nowrap;
    border-bottom: 1px solid ${colors.grey10};
  `,

  SparklesIcon: styled(Icon)`
    font-size: 0.875rem;
    margin-top: -0.125rem;
  `,

  HeaderTitle: styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    line-height: 1;
  `,

  BetaTag: styled.span`
    display: flex;
    align-items: center;
    font-size: 0.625rem;
    font-weight: ${weights.bold};
    text-transform: uppercase;
    color: ${colors.white};
    background: ${colors.primary};
    padding: 0.1875rem 0.3125rem;
    border-radius: 0.3125rem;
    line-height: 1;
  `,

  CollapseButton: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.9375rem;
    height: 1.875rem;
    min-width: 1.9375rem;
    padding: 0;
    border: none;
    border-radius: 0.25rem;
    background: transparent;
    color: ${colors.grey40};
    cursor: pointer;
    font-size: 0.75rem;
    margin-left: auto;
    flex-shrink: 0;

    &:hover {
      background: ${colors.grey10};
      color: ${({ $danger }) => ($danger ? colors.danger : colors.primary)};
    }
  `,

  CollapsedClickArea: styled.div`
    flex: 1;
    cursor: pointer;
  `,

  CollapsedHeader: styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 2.625rem;
    flex-shrink: 0;
    border-bottom: 1px solid ${colors.grey10};
  `,

  CollapsedToggle: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    min-width: 1.75rem;
    border: none;
    border-radius: 0.25rem;
    background: transparent;
    color: ${colors.primary};
    cursor: pointer;
    font-size: 0.875rem;
    padding: 0;

    .expand-icon {
      display: none;
      font-size: 0.75rem;
      color: ${colors.grey40};
    }

    &:hover {
      background: ${colors.grey10};

      .sparkles-icon {
        display: none;
      }
      .expand-icon {
        display: block;
        color: ${colors.primary};
      }
    }
  `,

  CollapsedBody: styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.4375rem 0;
  `,

  CollapsedNewChatButton: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border: none;
    border-radius: 0.25rem;
    background: transparent;
    color: ${colors.grey40};
    cursor: pointer;
    font-size: 0.75rem;
    flex-shrink: 0;
    padding: 0;

    &:hover {
      background: ${colors.grey10};
      color: ${colors.primary};
    }
  `,

  ExpandedBody: styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-width: 0;
  `,

  NewChatButton: styled.button`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem 0.5rem 1.6875rem;
    margin: 1rem 0 0;
    border: none;
    border-radius: 0;
    background: transparent;
    background-color: transparent;
    color: ${colors.black};
    font-size: 0.875rem;
    font-weight: ${weights.normal};
    cursor: pointer;
    flex-shrink: 0;
    height: auto;
    white-space: nowrap;

    .icon {
      font-size: 0.875rem;
      color: ${colors.grey40};
    }

    &:hover:not(:disabled) {
      background: ${colors.grey10};
      background-color: ${colors.grey10};
      color: ${colors.black};
    }

    &:disabled {
      color: ${colors.grey25};
      background: transparent;
      background-color: transparent;
      cursor: default;

      .icon {
        color: ${colors.grey10};
      }
    }
  `,

  SectionHeader: styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.75rem 0.25rem 1.6875rem;
    margin-top: 1rem;
    cursor: pointer;
    user-select: none;

    &:hover {
      color: ${colors.grey75};
    }
  `,

  SectionTitle: styled.span`
    font-size: 0.625rem;
    font-weight: ${weights.bold};
    color: ${colors.grey40};
    text-transform: uppercase;
    letter-spacing: 0.03em;
  `,

  SectionChevron: styled.div`
    display: flex;
    align-items: center;
    font-size: 0.5rem;
    color: ${colors.grey40};
    transition: transform 150ms ease;
    transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
    margin-top: -0.0625rem;
  `,

  ConversationList: styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 0.3125rem 0.5rem 0.3125rem 1.1875rem;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: ${colors.grey25};
      border-radius: 2px;
    }
    display: flex;
    flex-direction: column;
    gap: 0.3125rem;
  `,

  ConversationItem: styled.div`
    display: flex;
    align-items: center;
    padding: 0.5625rem 0.5rem;
    border-radius: 0.3125rem;
    cursor: pointer;
    position: relative;

    ${({ $menuOpen }) => $menuOpen && `background: ${colors.grey10};`}

    &:hover {
      background: ${colors.grey10};
    }

    &:hover .conversation-actions {
      opacity: 1;
    }
  `,

  ConversationTitle: styled.span`
    font-size: 0.75rem;
    line-height: 1;
    color: ${({ $active }) => ($active ? colors.primary : colors.black)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  `,

  ConversationActions: styled.div`
    opacity: ${({ $menuOpen }) => ($menuOpen ? 1 : 0)};
    flex-shrink: 0;
    margin-left: 0.25rem;
    height: 0;
    display: flex;
    align-items: center;
    overflow: visible;
  `,

  ActionButton: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    min-width: 1.5rem;
    padding: 0;
    border: none;
    border-radius: 0.25rem;
    background: transparent;
    color: ${colors.black};
    cursor: pointer;
    font-size: 0.625rem;

    &:hover {
      background: ${({ $menuOpen }) => ($menuOpen ? 'transparent' : colors.grey20)};
      color: ${colors.black};
    }
  `,

  ConversationDropdown: styled(Dropdown)`
    .conversation-menu {
      right: -0.5rem;
    }

    .conversation-menu > div {
      border-radius: 0.5rem;
      box-shadow: 0 0 0.875rem rgba(0, 0, 0, 0.25);
      padding: 0.875rem 0;
      margin-top: 0.25rem;
      position: relative;
      overflow: visible;

      &::before {
        content: '';
        position: absolute;
        top: -0.375rem;
        right: 0.625rem;
        width: 1.3125rem;
        height: 0.8125rem;
        background: ${colors.white};
        clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
        filter: drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.08));
      }
    }

    .conversation-menu-item {
      font-size: 0.75rem;
      padding: 0.375rem 1.25rem;
    }

    .conversation-menu-item--delete {
      color: ${colors.danger};
    }
  `,

  StreamingDot: styled.div`
    position: absolute;
    left: -0.375rem;
    width: 0.375rem;
    height: 0.375rem;
    border-radius: 50%;
    background: ${colors.primary};
    animation: ${pulse} 1.5s ease-in-out infinite;
  `,

  AdminIcon: styled(Icon)`
    font-size: 0.6875rem;
    color: ${colors.grey40};
    margin-right: 0.375rem;
    flex-shrink: 0;
  `,

  RenameInput: styled.input`
    font-size: 0.8125rem;
    color: ${colors.grey75};
    border: 1px solid ${colors.primary};
    border-radius: 0.25rem;
    padding: 0.125rem 0.25rem;
    outline: none;
    width: 100%;
    background: ${colors.white};
  `,
};

function MenuOpenSync({ isOpen, setMenuOpen }) {
  useEffect(() => {
    setMenuOpen(isOpen);
  }, [isOpen, setMenuOpen]);
  return null;
}

function ConversationItemRow({ conversation, isActive, isStreaming, onSelect, onRename, onDelete }) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(conversation.title);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleRenameSubmit = () => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== conversation.title) {
      onRename(conversation.id, trimmed);
    }
    setIsRenaming(false);
  };

  if (isRenaming) {
    return (
      <S.ConversationItem $active={isActive}>
        <S.RenameInput
          autoFocus
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onBlur={handleRenameSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleRenameSubmit();
            if (e.key === 'Escape') setIsRenaming(false);
          }}
        />
      </S.ConversationItem>
    );
  }

  return (
    <S.ConversationItem $active={isActive} $menuOpen={menuOpen} onClick={() => onSelect(conversation.id)}>
      {isStreaming && <S.StreamingDot />}
      {conversation.initiator === 'admin' && (
        <S.AdminIcon
          icon="user-secret"
          title={conversation.initiatorName ? `Started by ${conversation.initiatorName}` : 'Started by Ruddr Admin'}
        />
      )}
      <S.ConversationTitle $active={isActive} title={conversation.title}>
        {conversation.title}
      </S.ConversationTitle>
      <S.ConversationActions className="conversation-actions" $menuOpen={menuOpen}>
        <S.ConversationDropdown align="right">
          {({ setIsOpen: setDropdownOpen, isOpen: dropdownOpen }) => (
            <>
              <Dropdown.Trigger>
                <S.ActionButton
                  $menuOpen={menuOpen}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDropdownOpen(true);
                  }}>
                  <Icon icon="ellipsis-h" />
                </S.ActionButton>
              </Dropdown.Trigger>
              <Dropdown.Menu className="conversation-menu" onClick={(e) => e.stopPropagation()}>
                <Dropdown.Item
                  className="conversation-menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsRenaming(true);
                    setRenameValue(conversation.title);
                    setDropdownOpen(false);
                  }}>
                  Rename
                </Dropdown.Item>
                <Dropdown.Item
                  className="conversation-menu-item conversation-menu-item--delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(conversation.id);
                    setDropdownOpen(false);
                  }}>
                  Delete
                </Dropdown.Item>
              </Dropdown.Menu>
              <MenuOpenSync isOpen={dropdownOpen} setMenuOpen={setMenuOpen} />
            </>
          )}
        </S.ConversationDropdown>
      </S.ConversationActions>
    </S.ConversationItem>
  );
}

export default function AssistantSidebar({
  mode,
  conversations,
  activeConversationId,
  thinking,
  onSelectConversation,
  onNewChat,
  onRenameConversation,
  onDeleteConversation,
  sidebarOpen,
  onToggleSidebar,
  slideOut,
}) {
  const [historyOpen, setHistoryOpen] = useState(true);

  const isPanel = mode === 'panel';

  // Animate overlay close before unmounting
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const prevSidebarOpen = useRef(sidebarOpen);

  useEffect(() => {
    if (isPanel && prevSidebarOpen.current && !sidebarOpen) {
      setClosing(true);
      const timer = setTimeout(() => {
        setClosing(false);
        setOverlayVisible(false);
      }, 200);
      prevSidebarOpen.current = sidebarOpen;
      return () => clearTimeout(timer);
    }
    if (isPanel && sidebarOpen) {
      setOverlayVisible(true);
      setClosing(false);
    }
    prevSidebarOpen.current = sidebarOpen;
  }, [isPanel, sidebarOpen]);

  // In panel mode, keep expanded content visible during close animation
  const showExpanded = sidebarOpen || (isPanel && closing);

  const sidebarContent = showExpanded ? (
    <>
      <S.ExpandedHeader>
        <S.SparklesIcon icon="sparkles" color={colors.primary} />
        <S.HeaderTitle>Ask Ruddr</S.HeaderTitle>
        <S.BetaTag>beta</S.BetaTag>
        <S.CollapseButton $danger={isPanel} onClick={onToggleSidebar} title={isPanel ? 'Close' : 'Collapse sidebar'}>
          <Icon icon={isPanel ? 'xmark' : 'sidebar'} />
        </S.CollapseButton>
      </S.ExpandedHeader>
      <S.ExpandedBody>
        <S.NewChatButton onClick={onNewChat} disabled={!activeConversationId}>
          <Icon icon="edit" type="far" />
          New Chat
        </S.NewChatButton>

        {conversations.length > 0 && (
          <>
            <S.SectionHeader onClick={() => setHistoryOpen((prev) => !prev)}>
              <S.SectionTitle>Chat History</S.SectionTitle>
              <S.SectionChevron $open={historyOpen}>
                <Icon icon="chevron-down" />
              </S.SectionChevron>
            </S.SectionHeader>

            {historyOpen && (
              <S.ConversationList>
                {conversations.map((c) => (
                  <ConversationItemRow
                    key={c.id}
                    conversation={c}
                    isActive={c.id === activeConversationId}
                    isStreaming={c.responseStatus === 'streaming' || (c.id === activeConversationId && thinking)}
                    onSelect={onSelectConversation}
                    onRename={onRenameConversation}
                    onDelete={onDeleteConversation}
                  />
                ))}
              </S.ConversationList>
            )}
          </>
        )}
      </S.ExpandedBody>
    </>
  ) : (
    <>
      <S.CollapsedHeader>
        <S.CollapsedToggle onClick={onToggleSidebar} title="Expand sidebar">
          <Icon icon="sparkles" className="sparkles-icon" />
          <Icon icon="sidebar" className="expand-icon" />
        </S.CollapsedToggle>
      </S.CollapsedHeader>
      <S.CollapsedBody>
        <S.CollapsedNewChatButton onClick={onNewChat} title="Start New Chat">
          <Icon icon="plus" />
        </S.CollapsedNewChatButton>
      </S.CollapsedBody>
      <S.CollapsedClickArea onClick={onToggleSidebar} />
    </>
  );

  if (isPanel && !overlayVisible && !sidebarOpen) return null;

  return (
    <S.Wrapper $expanded={sidebarOpen} $overlay={isPanel} $slideOut={slideOut}>
      {isPanel ? (
        <>
          <S.OverlayScrim $closing={closing} onClick={onToggleSidebar} />
          <S.OverlayContent $expanded $closing={closing}>
            {sidebarContent}
          </S.OverlayContent>
        </>
      ) : (
        sidebarContent
      )}
    </S.Wrapper>
  );
}
